import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { ArrowDownTrayIcon, EnvelopeIcon, FunnelIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { LeadStatusBadge, LeadRatingBadge } from '../../components/ui/StatusBadge'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import type { Lead, LeadInput } from '../../../shared/types'

const leadsApi = createCrudApi<Lead, LeadInput>('leads')

const STATUSES = ['new', 'contacted', 'qualified', 'unqualified'] as const
type Status = (typeof STATUSES)[number]

export function MailingListExportPage() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all')
  const [tierFilter, setTierFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [salesPlayFilter, setSalesPlayFilter] = useState<string>('all')
  const [ratingFilter, setRatingFilter] = useState<string>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState(false)

  // Build filters
  const filters: Record<string, string> = {}
  if (statusFilter !== 'all') filters.status = statusFilter
  if (tierFilter !== 'all') filters.tier = tierFilter
  if (categoryFilter !== 'all') filters.category = categoryFilter
  if (salesPlayFilter !== 'all') filters.salesPlay = salesPlayFilter
  if (ratingFilter !== 'all') filters.rating = ratingFilter
  const hasFilters = Object.keys(filters).length > 0

  // Fetch all pages of leads for selection
  const { data: response, isLoading } = useQuery({
    queryKey: ['leads-export', { filters }],
    queryFn: async () => {
      const allLeads: Lead[] = []
      let currentPage = 1
      let totalPages = 1
      while (currentPage <= totalPages) {
        const res = await leadsApi.list({ page: currentPage, limit: 100, filters: hasFilters ? filters : undefined })
        allLeads.push(...res.data)
        totalPages = res.totalPages
        currentPage++
      }
      return { data: allLeads, total: allLeads.length }
    },
  })

  const leads = response?.data || []
  const leadsWithEmail = useMemo(() => leads.filter((l) => l.email), [leads])
  const leadsWithoutEmail = leads.length - leadsWithEmail.length

  // Selection handlers
  const allSelected = leadsWithEmail.length > 0 && leadsWithEmail.every((l) => selectedIds.has(l.id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(leadsWithEmail.map((l) => l.id)))
    }
  }

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  // Reset selection when filters change
  const handleFilterChange = <T,>(setter: (val: T) => void) => (val: T) => {
    setter(val)
    setSelectedIds(new Set())
  }

  const handleExport = async () => {
    if (selectedIds.size === 0) return
    setExporting(true)
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/leads/export/mailing-list`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      })
      if (!res.ok) throw new Error('Export failed')

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `mailing-list-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  const FilterChips = ({
    label,
    options,
    value,
    onChange,
  }: {
    label: string
    options: string[]
    value: string
    onChange: (v: string) => void
  }) => (
    <div>
      <h3 className="text-sm font-medium text-gray-900 mb-2">{label}</h3>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
              value === opt ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {opt === 'all' ? 'All' : opt}
          </button>
        ))}
      </div>
    </div>
  )

  if (isLoading) return <div className="p-8">{t('common.loading')}</div>

  return (
    <>
      {/* Sidebar - Filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <EnvelopeIcon className="h-5 w-5 text-brand-500" />
              Mailing List Export
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Filter leads, select recipients, and export a Brevo-compatible CSV.
            </p>
          </div>

          {/* Filter section */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <FunnelIcon className="h-4 w-4" />
              Filters
            </h3>

            <FilterChips
              label={t('fields.status')}
              options={['all', ...STATUSES]}
              value={statusFilter}
              onChange={handleFilterChange(setStatusFilter) as (v: string) => void}
            />

            <FilterChips
              label="Rating"
              options={['all', 'hot', 'warm', 'cold']}
              value={ratingFilter}
              onChange={handleFilterChange(setRatingFilter)}
            />

            <FilterChips
              label="Tier"
              options={['all', 'Tier 1', 'Tier 2']}
              value={tierFilter}
              onChange={handleFilterChange(setTierFilter)}
            />

            <FilterChips
              label="Sales Play"
              options={['all', 'Greenfield', 'Portal Upgrade', 'E-Commerce Int', 'Rip & Replace']}
              value={salesPlayFilter}
              onChange={handleFilterChange(setSalesPlayFilter)}
            />
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Export Summary</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Filtered leads</dt>
                <dd className="font-medium text-gray-900">{leads.length}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">With email</dt>
                <dd className="font-medium text-green-600">{leadsWithEmail.length}</dd>
              </div>
              {leadsWithoutEmail > 0 && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">No email (excluded)</dt>
                  <dd className="font-medium text-gray-400">{leadsWithoutEmail}</dd>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between text-sm">
                <dt className="text-gray-900 font-medium">Selected for export</dt>
                <dd className="font-semibold text-brand-600">{selectedIds.size}</dd>
              </div>
            </dl>
          </div>

          {/* Export button */}
          <button
            onClick={handleExport}
            disabled={selectedIds.size === 0 || exporting}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2.5 text-sm font-semibold text-white shadow-sm bg-brand-500 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            {exporting
              ? 'Exporting...'
              : selectedIds.size > 0
                ? `Export ${selectedIds.size} leads as CSV`
                : 'Select leads to export'}
          </button>
        </div>
      </aside>

      {/* Main content - Table */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Mailing List Export</h1>
            <p className="mt-1 text-sm text-gray-500">{leads.length} leads found, {leadsWithEmail.length} with email</p>

            {/* Mobile export button */}
            <button
              onClick={handleExport}
              disabled={selectedIds.size === 0 || exporting}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-brand-500 hover:opacity-90 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {selectedIds.size > 0 ? `Export ${selectedIds.size} leads` : 'Select leads to export'}
            </button>
          </div>

          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-x-auto shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3.5 text-left">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-600"
                          />
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.name')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.organisation')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.email')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.status')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.rating')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Tier</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.category')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {leads.map((lead) => {
                        const hasEmail = !!lead.email
                        const isSelected = selectedIds.has(lead.id)
                        return (
                          <tr
                            key={lead.id}
                            className={`transition-colors ${
                              !hasEmail
                                ? 'opacity-40'
                                : isSelected
                                  ? 'bg-brand-50'
                                  : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-3 py-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={!hasEmail}
                                onChange={() => toggleOne(lead.id)}
                                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-600 disabled:opacity-30"
                              />
                            </td>
                            <td className="whitespace-nowrap py-4 px-3 text-sm font-medium text-gray-900">
                              <div className="flex items-center gap-x-3">
                                <Avatar
                                  firstName={lead.lastName ? lead.firstName : null}
                                  lastName={lead.lastName || lead.company}
                                  size="sm"
                                />
                                <span>
                                  {lead.lastName
                                    ? `${lead.firstName || ''} ${lead.lastName}`.trim()
                                    : lead.company}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500 max-w-[200px]">
                              <span className="block truncate" title={lead.company}>
                                {lead.company}
                              </span>
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500 max-w-[220px]">
                              <span className="block truncate" title={lead.email || ''}>
                                {lead.email || <span className="italic text-gray-300">No email</span>}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <LeadStatusBadge status={lead.status} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <LeadRatingBadge rating={lead.rating} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              {lead.tier && (
                                <span
                                  className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                    lead.tier === 'Tier 1'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {lead.tier}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500 max-w-[150px]">
                              <span className="block truncate" title={lead.category || ''}>
                                {lead.category || '-'}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                      {leads.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-3 py-12 text-center text-sm text-gray-500">
                            No leads match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
